import { useState, useCallback } from 'react'
import { useAppDispatch } from '@state/hooks'
import { createCompanyFromBackend } from '@state/slices/companiesSlice'
import { onboardingApi } from '@services/onboardingApi'
import type {
  NaicsSuggestion,
  DataReadinessResponse,
  CompanyCreateResponse,
} from '@services/onboardingApi'
import NaicsConfirmation from './NaicsConfirmation'
import DataReadinessCard from './DataReadinessCard'
import './OnboardingWizard.css'

interface OnboardingWizardProps {
  onClose: () => void
  onComplete: (companyId: number) => void
}

const REVENUE_TIERS = ['<500K', '500K-2M', '2M-10M', '10M-50M', '50M+']

function OnboardingWizard({ onClose, onComplete }: OnboardingWizardProps) {
  const dispatch = useAppDispatch()
  const [step, setStep] = useState(1)
  const [error, setError] = useState<string | null>(null)

  // Step 1 state
  const [name, setName] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [revenueTier, setRevenueTier] = useState('')
  const [employeeCount, setEmployeeCount] = useState('')
  const [industryHint, setIndustryHint] = useState('')
  const [naicsSuggestions, setNaicsSuggestions] = useState<NaicsSuggestion[]>([])
  const [naicsLoading, setNaicsLoading] = useState(false)
  const [selectedNaics, setSelectedNaics] = useState<string | null>(null)
  const [showNaics, setShowNaics] = useState(false)

  // Result state
  const [createResult, setCreateResult] = useState<CompanyCreateResponse | null>(null)
  const [readiness, setReadiness] = useState<DataReadinessResponse | null>(null)
  const [readinessLoading, setReadinessLoading] = useState(false)
  const [creating, setCreating] = useState(false)

  const handleClassifyIndustry = useCallback(async () => {
    if (!industryHint.trim()) return
    setNaicsLoading(true)
    setShowNaics(true)
    try {
      const result = await onboardingApi.classifyIndustry(industryHint, city, state)
      setNaicsSuggestions(result.suggestions)
    } catch {
      setNaicsSuggestions([])
    } finally {
      setNaicsLoading(false)
    }
  }, [industryHint, city, state])

  const handleNaicsConfirm = (code: string) => {
    setSelectedNaics(code)
    setShowNaics(false)
  }

  const handleCreateCompany = async () => {
    if (!name.trim()) return
    setCreating(true)
    setError(null)
    try {
      const result = await onboardingApi.createCompany({
        name: name.trim(),
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        revenue_tier: revenueTier || undefined,
        industry_hint: industryHint.trim() || undefined,
        employee_count: employeeCount ? parseInt(employeeCount) : undefined,
        naics_code: selectedNaics || undefined,
      })
      setCreateResult(result)

      // Add to Redux
      dispatch(createCompanyFromBackend({
        id: `company_${result.company_id}`,
        name: name.trim(),
        industry: result.industry_label || industryHint.trim() || undefined,
        naics_code: result.naics_code || undefined,
        ivp_status: result.ivp_status,
        data_readiness_tier: 'identity',
      }))

      setStep(2)
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to create company')
    } finally {
      setCreating(false)
    }
  }

  const loadReadiness = async () => {
    if (!createResult) return
    setReadinessLoading(true)
    try {
      const r = await onboardingApi.getReadiness(createResult.company_id)
      setReadiness(r)
    } catch {
      // readiness check is non-critical
    } finally {
      setReadinessLoading(false)
    }
  }

  const handleGoToSummary = () => {
    loadReadiness()
    setStep(4)
  }

  const handleComplete = () => {
    if (createResult) {
      onComplete(createResult.company_id)
    }
  }

  return (
    <>
      <div className="wizard-overlay" onClick={onClose} />
      <div className="wizard-modal">
        <div className="wizard-header">
          <h3>New Company Setup</h3>
          <div className="wizard-steps">
            {['Identity', 'Financial', 'Operational', 'Summary'].map((label, i) => (
              <div key={label} className={`wizard-step ${step === i + 1 ? 'active' : ''} ${step > i + 1 ? 'done' : ''}`}>
                <span className="step-number">{i + 1}</span>
                <span className="step-label">{label}</span>
              </div>
            ))}
          </div>
          <button className="wizard-close" onClick={onClose}>x</button>
        </div>

        <div className="wizard-body">
          {error && <div className="wizard-error">{error}</div>}

          {/* Step 1: Identity */}
          {step === 1 && (
            <div className="wizard-step-content">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Better Hearing For You"
                  autoFocus
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Austin" />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input type="text" value={state} onChange={(e) => setState(e.target.value)} placeholder="TX" maxLength={2} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Revenue Tier</label>
                  <select value={revenueTier} onChange={(e) => setRevenueTier(e.target.value)}>
                    <option value="">Select...</option>
                    {REVENUE_TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Employee Count</label>
                  <input type="number" value={employeeCount} onChange={(e) => setEmployeeCount(e.target.value)} placeholder="e.g., 15" min={1} />
                </div>
              </div>
              <div className="form-group">
                <label>What does this company do?</label>
                <input
                  type="text"
                  value={industryHint}
                  onChange={(e) => setIndustryHint(e.target.value)}
                  onBlur={handleClassifyIndustry}
                  placeholder="e.g., dental practice, coffee shop, audiology clinic"
                />
              </div>

              {showNaics && (
                <NaicsConfirmation
                  suggestions={naicsSuggestions}
                  loading={naicsLoading}
                  onConfirm={handleNaicsConfirm}
                  onSkip={() => setShowNaics(false)}
                />
              )}

              {selectedNaics && !showNaics && (
                <div className="naics-selected">
                  NAICS: <strong>{selectedNaics}</strong>
                  <button className="btn-link" onClick={() => setShowNaics(true)}>Change</button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Financial Foundation */}
          {step === 2 && (
            <div className="wizard-step-content">
              <p className="step-description">
                Upload financial data to unlock deeper analysis. You can skip this and add data later.
              </p>
              {createResult?.naics_code && (
                <div className="template-download">
                  <p>Download an import template tailored to your industry:</p>
                  <button
                    className="btn-secondary"
                    onClick={async () => {
                      try {
                        const { template } = await onboardingApi.getImportTemplate(createResult.naics_code!)
                        const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' })
                        const url = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = `import_template_${createResult.naics_code}.json`
                        a.click()
                        URL.revokeObjectURL(url)
                      } catch {
                        // template download is non-critical
                      }
                    }}
                  >
                    Download Template
                  </button>
                </div>
              )}
              <p className="step-hint">
                Use the Data Manager to import CSVs for sales, customers, and accounting data.
              </p>
            </div>
          )}

          {/* Step 3: Operational Depth */}
          {step === 3 && (
            <div className="wizard-step-content">
              <p className="step-description">
                The more data domains you populate, the deeper Causeway's analysis. You can add data anytime.
              </p>
              <div className="domain-checklist">
                {['Sales Transactions', 'Customers', 'General Ledger', 'Products/Services', 'Employees', 'Competitors', 'Suppliers'].map(domain => (
                  <div key={domain} className="domain-item">
                    <span className="domain-checkbox">&#x25CB;</span>
                    <span>{domain}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Summary */}
          {step === 4 && (
            <div className="wizard-step-content">
              <div className="summary-company">
                <h4>{name}</h4>
                {selectedNaics && <p className="summary-naics">NAICS: {selectedNaics}</p>}
                {createResult?.industry_label && <p className="summary-industry">{createResult.industry_label}</p>}
                {createResult?.ivp_status && (
                  <p className="summary-ivp">IVP: <span className={`ivp-status ivp-${createResult.ivp_status}`}>{createResult.ivp_status}</span></p>
                )}
              </div>
              <DataReadinessCard readiness={readiness} loading={readinessLoading} />
            </div>
          )}
        </div>

        <div className="wizard-footer">
          {step > 1 && step < 4 && (
            <button className="btn-secondary" onClick={() => setStep(step - 1)}>Back</button>
          )}
          <div className="wizard-footer-right">
            {step === 1 && (
              <button className="btn-primary" onClick={handleCreateCompany} disabled={!name.trim() || creating}>
                {creating ? 'Creating...' : 'Create Company'}
              </button>
            )}
            {step === 2 && (
              <>
                <button className="btn-secondary" onClick={() => setStep(3)}>Skip</button>
                <button className="btn-primary" onClick={() => setStep(3)}>Next</button>
              </>
            )}
            {step === 3 && (
              <>
                <button className="btn-secondary" onClick={handleGoToSummary}>Skip</button>
                <button className="btn-primary" onClick={handleGoToSummary}>Next</button>
              </>
            )}
            {step === 4 && (
              <button className="btn-primary" onClick={handleComplete}>Start Analyzing</button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default OnboardingWizard
